// App.js - Production Ready with Integrated Layout and Authentication
import React, { useState, useEffect } from 'react';
import { Button, Layout, Spin, Radio } from 'antd'; // Import Radio for color mode switcher
import MenuList from './components/MenuList.js';
import AFIPStatusBadge from './components/AFIPStatusBadge.js';
import Logo from './components/Logo.js';
import DynamicFavicon from './components/DynamicFavicon.js';
import { HashRouter, Route, Routes, useLocation, Navigate } from 'react-router-dom'; // Import Navigate for redirection

// Page Imports
import PanelPage from './pages/panelpage.js';
import DashboardPage from './pages/dashboardPage.js';
import Variables from './pages/variablesPage.js';
import RepoPage from './pages/databasepage.js';
// import NoPageError from './pages/nopageerror.js'; // Not used in routes, but keep if imported for other reasons
// import ManualPage from './pages/manualpage.js'; // Not used in routes, keep if imported for other reasons
import SearchPageBasic from './pages/searchpage.js';
import Analisis from './pages/analisisPage.js';
import EditorPage from './pages/producteditorpage.js';
import SignagePage from './pages/signagepage.js';
import KioskSearchPage from './pages/kioskSearchPage.js';
import SignageDisplayPage from './pages/signagedisplaypage.js';
import PrintQuotePage from './pages/printQuotePage.js';
import PrintStickerPage from './pages/printStickerPage.js';
import PrintRAPage from './pages/printRAPage.js';
import PrintPOSupplierPage from './pages/printPOSupplierPage.js';
import PrintA4InvoicePage from './pages/printA4InvoicePage.js';

import LoginPage from './pages/LoginPage.js'; // Import the new LoginPage component
import BinPage from './pages/binpage.js';
import StickerPage from './pages/stickerPage.js';
import InvoicesPage from './pages/invoicesPage.js';
import ClientsPage from './pages/clientsPage.js';
import CreditNotesPage from './pages/creditNotesPage.js';
import NotasCreditoPage from './pages/notasCreditoPage.js';
import PresupuestosPage from './pages/presupuestosPage.js';
import AFIPQueuePage from './pages/afipQueuePage.js';
import OrdenesCompraPage from './pages/ordenesCompraPage.js';
import ProveedoresPage from './pages/proveedoresPage.js';
import OperationReport from './components/operation/OperationReport.js';
import CajaPage from './pages/cajaPage.js';
import MonthlyReportPage from './pages/MonthlyReportPage.js';
import PackProductsPage from './pages/packProductsPage.js';
import SidebarCartV2 from './components/sidebarcart/SidebarCartV2.js'; // Global cart drawer

// Context Imports
import CartProvider from './context/CartProvider.js';
import { useAuth } from './context/AuthContext.js';
import AuthProvider from './context/AuthProvider.js';

// RBAC
import ProtectedRoute from './components/ProtectedRoute.js';
import { ROLES } from './permissions.js';

// CSS Imports
import './components/MenuList.css'; // Contains styles for Sider, MenuList, Logo, Toggle
import './components/ColorModeSwitcher.css'; // Contains styles for Color Mode Switcher

// Demo mode banner (tree-shaken in production build)
const DemoBanner = process.env.REACT_APP_DEMO_MODE === 'true'
    ? require('./mocks/DemoBanner').default
    : null;



// It's good practice to ensure index.css is imported at the very top level of your app, e.g., src/index.js
// If not, you might add: import './index.css'; here, but typically index.js handles it.


const { Sider } = Layout;

// Main App component that sets up the AuthProvider and Router
function App() {
    return (
        // Wrap the entire application with AuthProvider
        <AuthProvider>
            {/* Wrap the application content that needs routing with HashRouter */}
            <HashRouter>
                {/* AppContent will handle conditional rendering based on auth state and route */}
                <AppContent />
            </HashRouter>
        </AuthProvider>
    );
}

// Component that consumes auth state and handles conditional rendering
function AppContent() {
    const [collapsed, setCollapsed] = useState(false);
    const location = useLocation(); // Get the current location

    // Color mode state management with localStorage persistence
    const [colorMode, setColorMode] = useState(() => {
        const savedMode = localStorage.getItem('horizontepyme-color-mode');
        return savedMode || 'claro';
    });

    // Color mode configurations
    const colorModes = {
        claro: {
            primary: '#4775ba',
            variables: 'var(--color-primary-base)'
        },
        pastel: {
            primary: '#A05AFF',
            variables: 'var(--color-pastels-primary)'
        },
        oscuro: {
            primary: '#2A2D3E',
            variables: 'var(--color-dark-primary)'
        }
    };

    // Apply color mode and save to localStorage
    useEffect(() => {
        localStorage.setItem('horizontepyme-color-mode', colorMode);
        // Apply color mode by switching CSS variable sets
        applyColorMode(colorMode);
    }, [colorMode]);

    // Function to apply color mode by dynamically updating CSS variables
    const applyColorMode = (mode) => {
        const root = document.documentElement;
        
        // Reset all mode-specific overrides
        root.classList.remove('theme-claro', 'theme-pastel', 'theme-oscuro');
        
        // Apply the selected theme class
        root.classList.add(`theme-${mode}`);
        
        // You can also directly set CSS custom properties if needed
        // For now, the CSS class approach is more maintainable
    };

    // Handle color mode change
    const handleColorModeChange = (e) => {
        setColorMode(e.target.value);
    };

    // Consume the authentication state and functions
    const { isAuthenticated, isLoadingAuth, interceptorsReady, user } = useAuth();

    // Signage display page owns its own auth (device token or stored admin session).
    // Detected up here so both unauthenticated and authenticated branches can route
    // through to the SignageDisplayPage without main-app layout / login redirect.
    //
    // react-router@6 HashRouter puts the route in `pathname`, not `hash` (the
    // hash part of `location` is always empty under HashRouter). Falling back to
    // `window.location.hash` for extra safety.
    const currentPathname = location.pathname || '';
    const currentHashRaw  = (typeof window !== 'undefined' ? window.location.hash : '') || '';
    const isSignageDisplayRoute =
        currentPathname.includes('/signage/display') ||
        currentHashRaw.includes('/signage/display');

    // --- Conditional Rendering based on Auth State ---

    // 1. Show a loading indicator while authentication state is being determined (e.g., from localStorage)
    // Also wait for axios interceptors to be ready before rendering pages
    if (isLoadingAuth || (isAuthenticated && !interceptorsReady)) {
        console.log("AppContent: Loading authentication state or setting up interceptors...");
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                <Spin size="large" tip="Initializing application..." />
            </div>
        );
    }

    // 2. If not authenticated, check if this is a print-quote page with invoiceId
    // Allow direct access to print-quote page without authentication if invoiceId is present
    if (!isAuthenticated) {
        const hash = location.hash;
        const isPrintQuoteWithId = hash.includes('/print-quote') && hash.includes('invoiceId=');

        if (isSignageDisplayRoute) {
            console.log("AppContent: Allowing unauthenticated access to /signage/display");
            return (
                <Routes>
                    <Route path="/signage/display" element={<SignageDisplayPage />} />
                    <Route path="*" element={<Navigate to="/signage/display" replace />} />
                </Routes>
            );
        }

        if (isPrintQuoteWithId) {
            console.log("AppContent: Allowing unauthenticated access to print-quote with invoiceId");
            return (
                <Routes>
                    <Route path="/print-quote" element={<PrintQuotePage />} />
                    <Route path="*" element={<Navigate to="/login" replace />} />
                </Routes>
            );
        }
        
        console.log("AppContent: Not authenticated, rendering LoginPage.");
        return (
            <Routes>
                <Route path="/login" element={<LoginPage />} />
                {/* Redirect any other path to /login if not authenticated */}
                <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
        );
    }

    // 3. If authenticated, proceed with rendering the main application layout
    console.log("AppContent: Authenticated, rendering main layout.");

    // --- Existing logic for Print Page conditional rendering (now runs only if authenticated) ---
    const queryParams = new URLSearchParams(location.search);
    const isPrintPage = queryParams.get('isPrint') === 'true';

    console.log("AppContent location.hash:", location.hash);
    console.log("AppContent location.search:", location.search);
    console.log("AppContent location.pathname:", location.pathname);
    console.log("AppContent isPrintPage:", isPrintPage);

    // Check if this is print-stickers page - render without layout
    const isPrintStickersPage = location.pathname.includes('/print-stickers');
    console.log("AppContent isPrintStickersPage:", isPrintStickersPage);

    // Signage display page: render without Sider/Layout, even if authenticated.
    // (Kiosk should never see the admin sidebar.)
    if (isSignageDisplayRoute) {
        console.log("Rendering SignageDisplayPage only (no layout)");
        return (
            <Routes>
                <Route path="/signage/display" element={<SignageDisplayPage />} />
                <Route path="*" element={<Navigate to="/signage/display" replace />} />
            </Routes>
        );
    }
    
    if (isPrintStickersPage) {
        console.log("Rendering PrintStickerPage only (no layout)");
        return (
            <Routes>
                <Route path="/print-stickers" element={<PrintStickerPage />} />
                <Route path="*" element={<Navigate to="/print-stickers" replace />} />
            </Routes>
        );
    }

    // Check if this is a standalone print page - render without layout
    const isPrintRAPage = location.pathname.includes('/print-ra');
    const isPrintPOPage = location.pathname.includes('/print-po-supplier');
    const isTotemPage = location.pathname.includes('/Totem');

    if (isTotemPage) {
        console.log("Rendering KioskSearchPage only (no layout)");
        return (
            <Routes>
                <Route path="/Totem" element={<KioskSearchPage />} />
                <Route path="*" element={<Navigate to="/Totem" replace />} />
            </Routes>
        );
    }

    if (isPrintRAPage) {
        return (
            <Routes>
                <Route path="/print-ra" element={<PrintRAPage />} />
                <Route path="*" element={<Navigate to="/print-ra" replace />} />
            </Routes>
        );
    }

    const isPrintA4Page = location.pathname.includes('/print-a4');
    if (isPrintA4Page) {
        return (
            <Routes>
                <Route path="/print-a4" element={<PrintA4InvoicePage />} />
                <Route path="*" element={<Navigate to="/print-a4" replace />} />
            </Routes>
        );
    }

    if (isPrintPOPage) {
        return (
            <Routes>
                <Route path="/print-po-supplier" element={<PrintPOSupplierPage />} />
                <Route path="*" element={<Navigate to="/print-po-supplier" replace />} />
            </Routes>
        );
    }

    // Conditional rendering based on isPrintPage (still relevant for authenticated users)
    if (isPrintPage) {
        console.log("Rendering PrintQuotePage only (Authenticated User)");
        return (
            // Keep CartProvider here if PrintQuotePage needs it
            <CartProvider>
                {/* Ensure PrintQuotePage is mounted via its own route if it's meant to be a standalone print page */}
                {/* Note: If you expect /print-quote to be the ONLY thing rendered for `isPrint=true`,
                          this specific check might be a bit redundant if you have a catch-all route.
                          However, it's a clear way to short-circuit the full layout for print view.
                */}
                <Routes>
                    <Route path="/print-quote" element={<PrintQuotePage />} />
                    {/* Optionally, redirect if someone tries to navigate away from /print-quote while isPrint=true */}
                    <Route path="*" element={<Navigate to="/print-quote?isPrint=true" replace />} />
                </Routes>
            </CartProvider>
        );
    }

    console.log("Rendering full application layout (Authenticated User)");

    // If authenticated and not the print page, render the main application layout
    return (
        <>
        {/* The main Ant Design Layout component with dynamic classes for padding adjustments.
            It's crucial this component uses flexbox to align Sider and content. */}
        <Layout
            className={collapsed ? 'layout-sider-collapsed' : 'layout-sider-expanded'}
            style={{ minHeight: '100vh', display: 'flex', flexDirection: 'row' }} // Ensure Layout acts as a flex container
        >
            <DynamicFavicon />
            {/* Sider component - now directly applies the 'sidebar' class */}
            <Sider
                collapsed={collapsed}
                collapsible
                theme='light' // Match your design: 'light' or 'dark'
                trigger={null} // We manage trigger with a custom button
                className='sidebar' // This class handles fixed positioning, 100vh, overflow, and hidden scrollbars
                width={220}
                collapsedWidth={80}
            >
                <Logo collapsed={collapsed} onToggle={() => setCollapsed(!collapsed)} />
                
                {/* Color Mode Switcher - DISABLED */}
                {/*
                <div className='color-mode-switcher'>
                    <Radio.Group 
                        onChange={handleColorModeChange} 
                        value={colorMode}
                        size="small"
                    >
                        <Radio.Button 
                            value="claro"
                            data-mode="claro"
                            title="Claro - Default color scheme"
                        />
                        <Radio.Button 
                            value="pastel"
                            data-mode="pastel"
                            title="Pastel - Soft color scheme"
                        />
                        <Radio.Button 
                            value="oscuro"
                            data-mode="oscuro"
                            title="Oscuro - Dark color scheme"
                        />
                    </Radio.Group>
                </div>
                */}
                
                <AFIPStatusBadge collapsed={collapsed} /> {/* AFIP/ARCA status badge at top of sidebar */}
                <MenuList /> {/* This component handles its own internal menu structure */}
            </Sider>

            {/* Main content div - will contain all routes EXCEPT the standalone login/print pages */}
            {/* `flex: 1` ensures it takes the remaining horizontal space after the fixed Sider. */}
            {/* `overflowY: 'auto'` allows its content to scroll independently. */}
            <div className='app-div' style={{ flex: 1, overflowY: 'auto' }}>
                <CartProvider> {/* CartProvider now wraps the Routes for pages that need it */}
                    {/* Global Cart Drawer - Only visible on Buscador page for roles that can use the cart */}
                    {/* Note: ROLES.CUSTOMER (future kiosk role) does NOT get the cart */}
                    {!isPrintPage && (location.pathname === '/Buscador' || location.pathname === '/home') && user?.role !== ROLES.CUSTOMER && <SidebarCartV2 />}
                    
                    <Routes>
                        {/* Redirect root path to /Buscador after login */}
                        {/* ── Always accessible routes ── */}
                        <Route index element={<Navigate to="/Buscador" replace />} />
                        <Route path='/home' element={<SearchPageBasic />} />
                        <Route path='/Buscador' element={<SearchPageBasic />} />
                        {/* Print routes — accessible to all authenticated roles */}
                        <Route path='/print-quote' element={<PrintQuotePage />} />
                        <Route path='/print-stickers' element={<PrintStickerPage />} />
                        <Route path='/print-ra' element={<PrintRAPage />} />
                        <Route path='/print-po-supplier' element={<PrintPOSupplierPage />} />
                        <Route path='/print-a4' element={<PrintA4InvoicePage />} />

                        {/* ── Ventas — ClientAdministrator + ClientUser ── */}
                        <Route path='/Invoices' element={<InvoicesPage />} />
                        <Route path='/FacturaEnCola' element={<AFIPQueuePage />} />
                        <Route path='/Presupuestos' element={<PresupuestosPage />} />
                        <Route path='/NotasCredito' element={<CreditNotesPage />} />

                        {/* ── Clientes — ClientAdministrator + ClientUser ── */}
                        <Route path='/Clientes' element={<ClientsPage />} />
                        <Route path='/CuentasCorrientes' element={<Navigate to="/Clientes?tab=cuentas" replace />} />
                        <Route path='/CuentasAbiertas' element={<Navigate to="/Clientes?tab=cuentas-abiertas" replace />} />

                        {/* ── Manuales — ClientAdministrator + ClientUser ── */}
                        <Route path='/manual' element={<Navigate to="/Buscador" replace />} />

                        {/* ── Admin-only routes ── */}
                        <Route path='/Panel' element={
                            <ProtectedRoute allowedRoles={[ROLES.CLIENT_ADMIN]}>
                                <PanelPage />
                            </ProtectedRoute>
                        } />
                        <Route path='/Dashboard' element={
                            <ProtectedRoute allowedRoles={[ROLES.CLIENT_ADMIN]}>
                                <DashboardPage />
                            </ProtectedRoute>
                        } />
                        <Route path='/Caja' element={
                            <ProtectedRoute allowedRoles={[ROLES.CLIENT_ADMIN]}>
                                <CajaPage />
                            </ProtectedRoute>
                        } />
                        <Route path='/OperacionDiaria' element={
                            <ProtectedRoute allowedRoles={[ROLES.CLIENT_ADMIN]}>
                                <Navigate to="/Caja" replace />
                            </ProtectedRoute>
                        } />
                        <Route path='/OperacionReportes' element={
                            <ProtectedRoute allowedRoles={[ROLES.CLIENT_ADMIN]}>
                                <Navigate to="/Caja" replace />
                            </ProtectedRoute>
                        } />
                        <Route path='/operation-report/:date' element={
                            <ProtectedRoute allowedRoles={[ROLES.CLIENT_ADMIN]}>
                                <OperationReport />
                            </ProtectedRoute>
                        } />
                        <Route path='/monthly-report' element={
                            <ProtectedRoute allowedRoles={[ROLES.CLIENT_ADMIN]}>
                                <MonthlyReportPage />
                            </ProtectedRoute>
                        } />
                        <Route path='/Analisis' element={
                            <ProtectedRoute allowedRoles={[ROLES.CLIENT_ADMIN]}>
                                <Analisis />
                            </ProtectedRoute>
                        } />
                        <Route path='/Proveedores' element={
                            <ProtectedRoute allowedRoles={[ROLES.CLIENT_ADMIN]}>
                                <ProveedoresPage />
                            </ProtectedRoute>
                        } />
                        <Route path='/OrdenesCompra' element={
                            <ProtectedRoute allowedRoles={[ROLES.CLIENT_ADMIN]}>
                                <OrdenesCompraPage />
                            </ProtectedRoute>
                        } />
                        <Route path='/OrdenesCompraTest' element={
                            <ProtectedRoute allowedRoles={[ROLES.CLIENT_ADMIN]}>
                                <OrdenesCompraPage />
                            </ProtectedRoute>
                        } />
                        <Route path='/Variables' element={
                            <ProtectedRoute allowedRoles={[ROLES.CLIENT_ADMIN]}>
                                <Variables />
                            </ProtectedRoute>
                        } />
                        <Route path='/Stickers' element={
                            <ProtectedRoute allowedRoles={[ROLES.CLIENT_ADMIN]}>
                                <StickerPage />
                            </ProtectedRoute>
                        } />
                        <Route path='/Database' element={
                            <ProtectedRoute allowedRoles={[ROLES.CLIENT_ADMIN]}>
                                <RepoPage />
                            </ProtectedRoute>
                        } />
                        <Route path='/ProductEditor' element={
                            <ProtectedRoute allowedRoles={[ROLES.CLIENT_ADMIN]}>
                                <EditorPage />
                            </ProtectedRoute>
                        } />
                        <Route path='/PackProducts' element={
                            <ProtectedRoute allowedRoles={[ROLES.CLIENT_ADMIN]}>
                                <PackProductsPage />
                            </ProtectedRoute>
                        } />
                        <Route path='/ProductAudit' element={
                            <ProtectedRoute allowedRoles={[ROLES.CLIENT_ADMIN]}>
                                <BinPage />
                            </ProtectedRoute>
                        } />
                        <Route path='/signage' element={
                            <ProtectedRoute allowedRoles={[ROLES.CLIENT_ADMIN]}>
                                <SignagePage />
                            </ProtectedRoute>
                        } />

                        {/* Catch-all — redirects to /Buscador */}
                        <Route path='*' element={<Navigate to="/Buscador" replace />} />
                    </Routes>
                </CartProvider>
            </div>
        </Layout>
        <DemoBannerOverlay />
        </>
    );
}

// Demo banner overlay — rendered outside the main layout tree so it's always on top
function DemoBannerOverlay() {
    if (!DemoBanner) return null;
    return <DemoBanner />;
}

// Export the main App component which includes the AuthProvider and router
export default App;